import React, { useState, useEffect } from 'react';
import { 
  ArrowLeftIcon, 
  BuildingOfficeIcon, 
  PlusIcon,
  UsersIcon,
  ShieldCheckIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  XMarkIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import tenantService, { Tenant, CreateTenantData } from '../services/tenantService';
import tenantUserService, { TenantUser } from '../services/tenantUserService';
import userService from '../services/users';
import AddTenantUserModal from '../components/AddTenantUserModal';

// Permissions simplifiées pour éviter les problèmes d'import
const PERMISSIONS = {
  TENANT_MANAGEMENT_VIEW: 'tenant_management.view',
  TENANT_MANAGEMENT_CREATE: 'tenant_management.create',
  TENANT_MANAGEMENT_EDIT: 'tenant_management.edit',
  TENANT_MANAGEMENT_DELETE: 'tenant_management.delete',
  USER_TENANT_MANAGEMENT_VIEW: 'user_tenant_management.view',
};

// Composants de permissions simplifiés
const SimplePermissionGuard: React.FC<{ permission: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const SimplePermissionButton: React.FC<{ permission: string; children: React.ReactNode; onClick?: () => void; className?: string; disabled?: boolean; title?: string }> = ({ children, onClick, className, disabled, title }) => {
  return (
    <button 
      type="button" 
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }} 
      className={className} 
      disabled={disabled} 
      title={title}
    >
      {children}
    </button>
  );
};

// Interface Tenant est maintenant importée du service

const TenantManagementPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showSitesModal, setShowSitesModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    is_active: true
  });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [tenantDependencies, setTenantDependencies] = useState<any>(null);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [tenantToBlock, setTenantToBlock] = useState<Tenant | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [tenantToEdit, setTenantToEdit] = useState<Tenant | null>(null);
  const [editTenantFormData, setEditTenantFormData] = useState({
    name: '',
    code: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // États pour le formulaire de création de tenant
  const [newTenant, setNewTenant] = useState({
    name: '',
    code: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    owner_username: '',
    owner_email: '',
    owner_first_name: '',
    owner_last_name: '',
    owner_password: '',
    owner_password_confirm: '',
    // Site par défaut
    site_name: '',
    site_code: '',
    site_address: '',
    site_contact_email: '',
    site_contact_phone: ''
  });

  // Charger les tenants
  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      
    
      const tenantsData = await tenantService.getTenants();
     
      // S'assurer que tenantsData est un tableau
      if (Array.isArray(tenantsData)) {
        setTenants(tenantsData);
      } else if (tenantsData && typeof tenantsData === 'object' && 'data' in tenantsData && Array.isArray((tenantsData as any).data)) {
        setTenants((tenantsData as any).data);
      } else {
        setTenants([]);
      }
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement des tenants:', error);
      console.error('❌ Détails de l\'erreur:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      setError(error.message || 'Impossible de charger les tenants');
      setTenants([]); // S'assurer que tenants reste un tableau même en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  // Fonction pour charger les utilisateurs d'un tenant
  const loadTenantUsers = async (tenantId: number) => {
    try {
      setLoadingUsers(true);
      
      const users = await tenantUserService.getTenantUsers(tenantId);
      
      // S'assurer que users est un tableau et que chaque utilisateur a la structure attendue
      if (Array.isArray(users)) {
        
        const validUsers = users.filter(user => {
          
          
          // Vérifier différentes structures possibles
          const hasUserObject = user?.user && typeof user.user === 'object';
          const hasUserFields = user?.user_username || user?.user_email || user?.user_first_name;
          const hasBasicFields = user?.tenant_user_id && (user?.role || user?.user_id);
          
          const isValid = user && (user.id || user.tenant_user_id) && (hasUserObject || hasUserFields || hasBasicFields);
          
         
          
          return isValid;
        });
        
        setTenantUsers(validUsers);
      } else {
        console.warn('⚠️ Format de données inattendu pour les utilisateurs:', users);
        console.warn('⚠️ Type:', typeof users);
        console.warn('⚠️ Clés disponibles:', users ? Object.keys(users) : 'null/undefined');
        setTenantUsers([]);
      }
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement des utilisateurs:', error);
      console.error('❌ Détails de l\'erreur:', error.response?.data);
      setError(error.message);
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Supprimer/détacher un utilisateur du tenant
  const handleRemoveUserFromTenant = async (tenantUserId: number, _userId: number, username: string) => {
    if (!selectedTenant) return;
    
    const confirmMessage = `Êtes-vous sûr de vouloir détacher l'utilisateur "${username}" du tenant "${selectedTenant.name}" ?\n\nL'utilisateur restera dans la base de données mais ne sera plus associé à ce tenant.`;
    
    if (!confirm(confirmMessage)) return;
    
    try {
      await tenantUserService.removeUserFromTenant(tenantUserId);
     
      // Recharger la liste des utilisateurs
      await loadTenantUsers(selectedTenant.id);
      
      alert(`Utilisateur "${username}" détaché du tenant avec succès.`);
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de l\'utilisateur du tenant.');
    }
  };

  // Ouvrir la fiche utilisateur
  const handleOpenUserProfile = (userId: number, username: string) => {
  // Trouver l'utilisateur dans la liste
    const user = tenantUsers.find(tu => (tu as any).user_id === userId || tu.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowUserProfileModal(true);
    } else {
      alert(`Utilisateur "${username}" non trouvé dans la liste`);
    }
  };

  // Ouvrir le modal d'édition d'utilisateur
  const handleEditUser = (userId: number, username: string) => {
   
    // Trouver l'utilisateur dans la liste
    const user = tenantUsers.find(tu => (tu as any).user_id === userId || tu.id === userId);
    if (user) {
      const userData = user as any;
      setEditingUser(user);
      setEditFormData({
        username: userData.user_username || '',
        email: userData.user_email || '',
        first_name: userData.user_first_name || '',
        last_name: userData.user_last_name || '',
        is_active: userData.user_is_active !== undefined ? userData.user_is_active : true
      });
      setShowEditUserModal(true);
    } else {
      alert(`Utilisateur "${username}" non trouvé dans la liste`);
    }
  };

  // Sauvegarder les modifications d'utilisateur
  const handleSaveUserEdit = async () => {
    if (!editingUser) return;
    
    try {
      const userId = (editingUser as any).user_id || editingUser.id;
      await userService.updateUser(userId, editFormData);
      alert('Utilisateur modifié avec succès');
      
      // Fermer le modal d'édition
      setShowEditUserModal(false);
      setEditingUser(null);
      
      // Recharger la liste des utilisateurs
      if (selectedTenant) {
        await loadTenantUsers(selectedTenant.id);
      }
      
      // Fermer aussi le modal de fiche si ouvert
      setShowUserProfileModal(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('❌ Erreur lors de la modification:', error);
      alert('Erreur lors de la modification de l\'utilisateur');
    }
  };

  // Ouvrir le modal de changement de mot de passe
  const handleChangePassword = (userId: number, username: string) => {
   
    // Trouver l'utilisateur dans la liste
    const user = tenantUsers.find(tu => (tu as any).user_id === userId || tu.id === userId);
    if (user) {
      setEditingUser(user);
      setPasswordFormData({
        new_password: '',
        confirm_password: ''
      });
      setShowChangePasswordModal(true);
    } else {
      alert(`Utilisateur "${username}" non trouvé dans la liste`);
    }
  };

  // Ouvrir le modal de blocage de tenant
  const handleBlockTenant = (tenant: Tenant) => {
    
    setTenantToBlock(tenant);
    setBlockReason('');
    setShowBlockModal(true);
  };

  // Confirmer le blocage d'un tenant
  const handleConfirmBlock = async () => {
    if (!tenantToBlock || !blockReason.trim()) {
      alert('Veuillez fournir une raison pour le blocage');
      return;
    }
    
    try {
      await tenantService.blockTenant(tenantToBlock.id, blockReason);
      alert(`Tenant "${tenantToBlock.name}" bloqué et désactivé avec succès.`);
      
      // Fermer le modal et recharger la liste
      setShowBlockModal(false);
      setTenantToBlock(null);
      setBlockReason('');
      loadTenants();
    } catch (error: any) {
      console.error('❌ Erreur lors du blocage:', error);
      alert(`Erreur lors du blocage: ${error.message}`);
    }
  };

  // Débloquer un tenant
  const handleUnblockTenant = async (tenant: Tenant) => {
    const confirmMessage = `Êtes-vous sûr de vouloir débloquer le tenant "${tenant.name}" ?\n\n` +
      `Le tenant pourra à nouveau être utilisé normalement.\n` +
      `${tenant.block_reason ? `\nRaison du blocage précédent: ${tenant.block_reason}` : ''}`;
    
    if (!confirm(confirmMessage)) return;
    
    try {
      await tenantService.unblockTenant(tenant.id);
      alert(`✅ Tenant "${tenant.name}" débloqué et réactivé avec succès.\n\nLe tenant est maintenant actif et peut être utilisé.`);
      loadTenants();
    } catch (error: any) {
      console.error('❌ Erreur lors du déblocage:', error);
      alert(`❌ Erreur lors du déblocage: ${error.message}`);
    }
  };

  // Ouvrir le modal de suppression de tenant
  const handleDeleteTenant = async (tenant: Tenant) => {
    
    setTenantToDelete(tenant);
    setDeleteReason('');
    setTenantDependencies(null);
    setShowDeleteModal(true);
    
    // Charger les dépendances
    try {
      setLoadingDependencies(true);
      const dependencies = await tenantService.getTenantDependencies(tenant.id);
      setTenantDependencies(dependencies);
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement des dépendances:', error);
      setTenantDependencies(null);
    } finally {
      setLoadingDependencies(false);
    }
  };

  // Ouvrir le modal de modification de tenant
  const handleEditTenant = (tenant: Tenant) => {
    
    setTenantToEdit(tenant);
    setEditTenantFormData({
      name: tenant.name || '',
      code: tenant.code || '',
      description: tenant.description || '',
      contact_email: tenant.contact_email || '',
      contact_phone: tenant.contact_phone || '',
      address: tenant.address || ''
    });
    setShowEditTenantModal(true);
  };

  // Sauvegarder les modifications du tenant
  const handleSaveTenantEdit = async () => {
    if (!tenantToEdit) return;
    
    try {
      setIsSubmittingEdit(true);
      
      const updatedTenant = await tenantService.updateTenant(tenantToEdit.id, editTenantFormData);
      
      alert(`Tenant "${updatedTenant.name}" modifié avec succès`);
      
      // Fermer le modal et recharger la liste
      setShowEditTenantModal(false);
      setTenantToEdit(null);
      setEditTenantFormData({
        name: '',
        code: '',
        description: '',
        contact_email: '',
        contact_phone: '',
        address: ''
      });
      loadTenants();
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la modification du tenant:', error);
      alert(`Erreur lors de la modification: ${error.message}`);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Confirmer la suppression/désactivation
  const handleConfirmDelete = async () => {
    if (!tenantToDelete) return;
    
    try {
      // Vérifier que la suppression est possible
      if (tenantDependencies?.can_delete !== true) {
        alert(`Impossible de supprimer définitivement le tenant "${tenantToDelete.name}". Il contient des dépendances actives. Utilisez le blocage pour désactiver le tenant.`);
        return;
      }
      
      // Suppression définitive uniquement
      await tenantService.hardDeleteTenant(tenantToDelete.id, deleteReason);
      alert(`Tenant "${tenantToDelete.name}" supprimé définitivement avec succès.`);
      
      // Fermer le modal et recharger la liste
      setShowDeleteModal(false);
      setTenantToDelete(null);
      setTenantDependencies(null);
      setDeleteReason('');
      loadTenants();
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression:', error);
      alert(`Erreur: ${error.message}`);
    }
  };

  // Sauvegarder le nouveau mot de passe
  const handleSavePasswordChange = async () => {
    if (!editingUser) return;
    
    // Validation côté frontend
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordFormData.new_password.length < 8) {
      alert('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    try {
      const userId = (editingUser as any).user_id || editingUser.id;
      await userService.changePassword(
        userId, 
        passwordFormData.new_password, 
        passwordFormData.confirm_password
      );
 
      alert('Mot de passe modifié avec succès');
      
      // Fermer le modal de changement de mot de passe
      setShowChangePasswordModal(false);
      setEditingUser(null);
      setPasswordFormData({
        new_password: '',
        confirm_password: ''
      });
      
      // Fermer aussi le modal de fiche si ouvert
      setShowUserProfileModal(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('❌ Erreur lors du changement de mot de passe:', error);
      alert('Erreur lors du changement de mot de passe');
    }
  };

  // Filtrer les tenants
  const filteredTenants = Array.isArray(tenants) ? tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Créer un nouveau tenant avec son propriétaire
  const handleCreateTenant = async () => {
    if (!newTenant.name || !newTenant.code || !newTenant.owner_username || !newTenant.owner_email) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (newTenant.owner_password !== newTenant.owner_password_confirm) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (!newTenant.site_name || !newTenant.site_code) {
      alert('Veuillez remplir les informations du site par défaut');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const createData: CreateTenantData = {
        tenant: {
          name: newTenant.name,
          code: newTenant.code,
          description: newTenant.description,
          address: newTenant.site_address,
          contact_email: newTenant.contact_email,
          contact_phone: newTenant.contact_phone
        },
        admin: {
          username: newTenant.owner_username,
          email: newTenant.owner_email,
          first_name: newTenant.owner_first_name,
          last_name: newTenant.owner_last_name,
          password: newTenant.owner_password
        },
        default_site: {
          name: newTenant.site_name,
          code: newTenant.site_code,
          site_type: 'WAREHOUSE', // Type par défaut
          address: newTenant.site_address,
          contact_email: newTenant.site_contact_email,
          contact_phone: newTenant.site_contact_phone
        }
      };

      const result = await tenantService.createTenant(createData);
      
      alert(`Tenant "${result.tenant.name}" créé avec succès !\nPropriétaire: ${result.admin.username}\nSite par défaut: ${result.default_site.name}`);
      setShowCreateModal(false);
      resetForm();
      loadTenants();
    } catch (error: any) {
      console.error('Erreur lors de la création du tenant:', error);
      alert(`Impossible de créer le tenant: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setNewTenant({
      name: '',
      code: '',
      description: '',
      contact_email: '',
      contact_phone: '',
      owner_username: '',
      owner_email: '',
      owner_first_name: '',
      owner_last_name: '',
      owner_password: '',
      owner_password_confirm: '',
      site_name: '',
      site_code: '',
      site_address: '',
      site_contact_email: '',
      site_contact_phone: ''
    });
  };

  return (
    <SimplePermissionGuard permission={PERMISSIONS.TENANT_MANAGEMENT_VIEW}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Link to="/dashboard" className="p-2 text-gray-400 hover:text-gray-600 mr-4">
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Tenants</h1>
              <p className="text-gray-600">Administration des organisations et de leurs propriétaires</p>
            </div>
          </div>
          <SimplePermissionButton
            permission={PERMISSIONS.TENANT_MANAGEMENT_CREATE}
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 font-medium shadow-sm hover:shadow transition bg-bright-orange text-white hover:bg-orange-600 focus:ring-2 focus:ring-orange-500/40 focus:outline-none"
          >
            <PlusIcon className="h-4 w-4" />
            Nouveau Tenant
          </SimplePermissionButton>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Erreur de connexion</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total tenants</p>
                <p className="text-3xl font-bold text-gray-900">{Array.isArray(tenants) ? tenants.length : 0}</p>
              </div>
            </div>
            <p className="text-sm text-blue-600">Organisations actives</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-2">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Tenants actifs</p>
                <p className="text-3xl font-bold text-gray-900">
                  {Array.isArray(tenants) ? tenants.filter(t => t.is_active && !t.is_blocked).length : 0}
                </p>
              </div>
            </div>
            <p className="text-sm text-green-600">
              {Array.isArray(tenants) && tenants.length > 0 ? Math.round((tenants.filter(t => t.is_active && !t.is_blocked).length / tenants.length) * 100) : 0}% du total
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center mb-2">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <UsersIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total utilisateurs</p>
                <p className="text-3xl font-bold text-gray-900">
                  {Array.isArray(tenants) ? tenants.reduce((sum, tenant) => sum + (tenant.users_count || 0), 0) : 0}
                </p>
              </div>
            </div>
            <p className="text-sm text-purple-600">Tous tenants confondus</p>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom, code ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {/* Table des tenants */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Liste des tenants</h3>
            {!loading && (
              <p className="text-sm text-gray-500 mt-1">
                {filteredTenants.length} tenant(s) trouvé(s)
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriétaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sites
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateurs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                        <span className="ml-3 text-gray-600">Chargement des tenants...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {error ? 'Erreur de connexion' : 'Aucun tenant trouvé'}
                    </td>
                  </tr>
                ) : (
                  filteredTenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-bright-orange rounded-full flex items-center justify-center mr-3">
                            <BuildingOfficeIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                            <div className="text-sm text-gray-500">{tenant.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {tenant.admin_info ? (
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                              <ShieldCheckIcon className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {tenant.admin_info.first_name} {tenant.admin_info.last_name}
                              </div>
                              <div className="text-sm text-gray-500">{tenant.admin_info.email}</div>
                              <div className="text-xs text-gray-400">@{tenant.admin_info.username}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Non assigné</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{tenant.sites_count || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{selectedTenant?.id === tenant.id ? (tenantUsers?.length || 0) : (tenant.users_count || 0)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          tenant.is_active && !tenant.is_blocked
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : tenant.is_blocked
                            ? 'bg-red-100 text-red-800 border border-red-200'
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                          {tenant.is_active && !tenant.is_blocked ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Actif
                            </>
                          ) : tenant.is_blocked ? (
                            <>
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Bloqué
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Inactif
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <SimplePermissionButton
                            permission={PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW}
                            onClick={() => {
                              setSelectedTenant(tenant);
                              loadTenantUsers(tenant.id);
                              setShowUsersModal(true);
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-600 hover:text-blue-900 hover:bg-blue-50 transition-colors focus:ring-2 focus:ring-blue-500/40 focus:outline-none"
                            title="Voir les utilisateurs"
                          >
                            <UsersIcon className="h-4 w-4" />
                          </SimplePermissionButton>
                          <SimplePermissionButton
                            permission={PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW}
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setShowAddUserModal(true);
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-green-600 hover:text-green-900 hover:bg-green-50 transition-colors focus:ring-2 focus:ring-green-500/40 focus:outline-none"
                            title="Ajouter un utilisateur"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                          </SimplePermissionButton>
                          <SimplePermissionButton
                            permission={PERMISSIONS.TENANT_MANAGEMENT_VIEW}
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setShowSitesModal(true);
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-purple-600 hover:text-purple-900 hover:bg-purple-50 transition-colors focus:ring-2 focus:ring-purple-500/40 focus:outline-none"
                            title="Voir les sites"
                          >
                            <BuildingOfficeIcon className="h-4 w-4" />
                          </SimplePermissionButton>
                          <SimplePermissionButton
                            permission={PERMISSIONS.TENANT_MANAGEMENT_EDIT}
                            onClick={() => handleEditTenant(tenant)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-orange-600 hover:text-orange-900 hover:bg-orange-50 transition-colors focus:ring-2 focus:ring-orange-500/40 focus:outline-none"
                            title="Modifier"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </SimplePermissionButton>
                          {tenant.is_blocked ? (
                            <SimplePermissionButton
                              permission={PERMISSIONS.TENANT_MANAGEMENT_EDIT}
                              onClick={() => handleUnblockTenant(tenant)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-green-600 hover:text-green-900 hover:bg-green-50 transition-colors focus:ring-2 focus:ring-green-500/40 focus:outline-none"
                              title="Débloquer le tenant"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </SimplePermissionButton>
                          ) : (
                            <SimplePermissionButton
                              permission={PERMISSIONS.TENANT_MANAGEMENT_EDIT}
                              onClick={() => handleBlockTenant(tenant)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 transition-colors focus:ring-2 focus:ring-yellow-500/40 focus:outline-none"
                              title="Bloquer le tenant"
                            >
                              <LockClosedIcon className="h-4 w-4" />
                            </SimplePermissionButton>
                          )}
                          <SimplePermissionButton
                            permission={PERMISSIONS.TENANT_MANAGEMENT_DELETE}
                            onClick={() => handleDeleteTenant(tenant)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 hover:text-red-900 hover:bg-red-50 transition-colors focus:ring-2 focus:ring-red-500/40 focus:outline-none"
                            title="Supprimer/Désactiver"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </SimplePermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal de création de tenant */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Créer un nouveau tenant</h2>
              
              <div className="space-y-6">
                {/* Informations du tenant */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du tenant</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom du tenant <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenant.name}
                        onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Nom de l'organisation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code du tenant <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenant.code}
                        onChange={(e) => setNewTenant({ ...newTenant, code: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="CODE"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={newTenant.description}
                        onChange={(e) => setNewTenant({ ...newTenant, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        rows={3}
                        placeholder="Description de l'organisation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email de contact
                      </label>
                      <input
                        type="email"
                        value={newTenant.contact_email}
                        onChange={(e) => setNewTenant({ ...newTenant, contact_email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="contact@organisation.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone de contact
                      </label>
                      <input
                        type="tel"
                        value={newTenant.contact_phone}
                        onChange={(e) => setNewTenant({ ...newTenant, contact_phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations du propriétaire */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Propriétaire du tenant (TENANT_OWNER)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom d'utilisateur <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenant.owner_username}
                        onChange={(e) => setNewTenant({ ...newTenant, owner_username: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="nom_utilisateur"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={newTenant.owner_email}
                        onChange={(e) => setNewTenant({ ...newTenant, owner_email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="proprietaire@organisation.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prénom
                      </label>
                      <input
                        type="text"
                        value={newTenant.owner_first_name}
                        onChange={(e) => setNewTenant({ ...newTenant, owner_first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Jean"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom de famille
                      </label>
                      <input
                        type="text"
                        value={newTenant.owner_last_name}
                        onChange={(e) => setNewTenant({ ...newTenant, owner_last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Dupont"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mot de passe <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={newTenant.owner_password}
                        onChange={(e) => setNewTenant({ ...newTenant, owner_password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Mot de passe sécurisé"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmer le mot de passe <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={newTenant.owner_password_confirm}
                        onChange={(e) => setNewTenant({ ...newTenant, owner_password_confirm: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Confirmer le mot de passe"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations du site par défaut */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Site par défaut</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom du site <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenant.site_name}
                        onChange={(e) => setNewTenant({ ...newTenant, site_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Nom du site principal"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code du site <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTenant.site_code}
                        onChange={(e) => setNewTenant({ ...newTenant, site_code: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="SITE_CODE"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adresse du site
                      </label>
                      <input
                        type="text"
                        value={newTenant.site_address}
                        onChange={(e) => setNewTenant({ ...newTenant, site_address: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Adresse complète du site"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email de contact du site
                      </label>
                      <input
                        type="email"
                        value={newTenant.site_contact_email}
                        onChange={(e) => setNewTenant({ ...newTenant, site_contact_email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="contact@site.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone de contact du site
                      </label>
                      <input
                        type="tel"
                        value={newTenant.site_contact_phone}
                        onChange={(e) => setNewTenant({ ...newTenant, site_contact_phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                  >
                    Annuler
                  </button>
                  <SimplePermissionButton
                    permission={PERMISSIONS.TENANT_MANAGEMENT_CREATE}
                    onClick={handleCreateTenant}
                    disabled={isSubmitting || !newTenant.name || !newTenant.code || !newTenant.owner_username || !newTenant.owner_email || !newTenant.owner_password || !newTenant.site_name || !newTenant.site_code}
                    className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-white bg-bright-orange hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Création...
                      </>
                    ) : (
                      <>
                        <UserPlusIcon className="h-4 w-4 mr-2" />
                        Créer le tenant et le propriétaire
                      </>
                    )}
                  </SimplePermissionButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal d'affichage des utilisateurs */}
        {showUsersModal && selectedTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Utilisateurs du tenant: {selectedTenant.name}</h2>
              
              <div className="space-y-6">
                {/* Admin du tenant */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Administrateur du tenant</h3>
                  {selectedTenant.admin_info ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                          <ShieldCheckIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <div className="text-lg font-medium text-green-900">
                            {selectedTenant.admin_info.first_name} {selectedTenant.admin_info.last_name}
                          </div>
                          <div className="text-sm text-green-700">{selectedTenant.admin_info.email}</div>
                          <div className="text-sm text-green-600">@{selectedTenant.admin_info.username}</div>
                          <div className="text-sm text-green-500">
                            Propriétaire du tenant
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                        <span className="text-yellow-800">Aucun administrateur assigné à ce tenant</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Liste des utilisateurs */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Tous les utilisateurs</h3>
                  {loadingUsers ? (
                    <div className="text-center text-gray-500 py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p>Chargement des utilisateurs...</p>
                    </div>
                  ) : tenantUsers && tenantUsers.length > 0 ? (
                    <div className="space-y-3">
                      {tenantUsers.map((tenantUser) => {
                        // Vérification supplémentaire pour éviter les erreurs
                        if (!tenantUser || (!tenantUser.id && !tenantUser.tenant_user_id)) {
                          console.warn('⚠️ Utilisateur invalide:', tenantUser);
                          return null;
                        }
                        
                        // Gérer toutes les structures possibles
                        const tenantUserAny = tenantUser as any;
                        
                        // Essayer différentes sources pour les données utilisateur
                        let userData: any = {};
                        
                        if (tenantUser.user && typeof tenantUser.user === 'object') {
                          // Structure imbriquée moderne
                          userData = tenantUser.user;
                        } else if (tenantUserAny.user_first_name || tenantUserAny.user_email) {
                          // Structure avec champs séparés
                          userData = {
                            first_name: tenantUserAny.user_first_name,
                            last_name: tenantUserAny.user_last_name,
                            email: tenantUserAny.user_email,
                            username: tenantUserAny.user_username,
                            is_active: tenantUserAny.user_is_active
                          };
                        } else {
                          // Structure alternative (anciens tenants)
                          userData = {
                            first_name: tenantUserAny.first_name || tenantUserAny.name?.split(' ')[0] || 'Prénom',
                            last_name: tenantUserAny.last_name || tenantUserAny.name?.split(' ').slice(1).join(' ') || 'Nom',
                            email: tenantUserAny.email || 'Email non renseigné',
                            username: tenantUserAny.username || 'username',
                            is_active: tenantUserAny.is_active !== undefined ? tenantUserAny.is_active : true
                          };
                        }
                        
                        return (
                        <div key={tenantUser.id || tenantUser.tenant_user_id || `user-${Math.random()}`} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-blue-600 font-medium text-sm">
                                  {(userData.first_name || 'U')[0]}{(userData.last_name || 'U')[0]}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {userData.first_name || 'Prénom'} {userData.last_name || 'Nom'}
                                </div>
                                <div className="text-sm text-gray-500">{userData.email || 'Email non renseigné'}</div>
                                <div className="text-sm text-gray-400">@{userData.username || 'username'}</div>
                                <div className={`text-xs ${userData.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                  {userData.is_active ? 'Actif' : 'Inactif'}
                                </div>
                                {/* Indicateur de structure pour debug */}
                                <div className="text-xs text-gray-400 mt-1">
                                  {tenantUser.user ? 'Structure moderne' : 
                                   tenantUserAny.user_first_name ? 'Structure séparée' : 
                                   'Structure ancienne'}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 items-center">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  tenantUser.role === 'TENANT_ADMIN'
                                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                    : tenantUser.role === 'TENANT_MANAGER'
                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                                }`}
                                title={`Rôle dans le tenant: ${tenantUser.role}`}
                              >
                                {tenantUser.role === 'TENANT_ADMIN' ? 'Administrateur' : 'Gestionnaire'}
                              </span>
                              
                              {/* Boutons d'action */}
                              <div className="flex gap-1 ml-2">
                                {/* Bouton Fiche utilisateur */}
                                <button
                                  type="button"
                                  onClick={() => handleOpenUserProfile(tenantUserAny.user_id || tenantUser.id, userData.username || 'utilisateur')}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  title="Voir la fiche utilisateur"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  Fiche
                                </button>
                                
              {/* Bouton Supprimer (sauf TENANT_OWNER) */}
              {(tenantUser.role as string) !== 'TENANT_OWNER' && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveUserFromTenant(
                                      tenantUserAny.tenant_user_id || tenantUser.id, 
                                      tenantUserAny.user_id || tenantUser.id, 
                                      userData.username || 'utilisateur'
                                    )}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                    title="Détacher du tenant"
                                  >
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Détacher
                                  </button>
                                )}
                                
              {/* Indicateur pour TENANT_OWNER */}
              {(tenantUser.role as string) === 'TENANT_OWNER' && (
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Propriétaire
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Informations supplémentaires */}
                          <div className="ml-13">
                            <div className="text-xs text-gray-500 mb-1">Informations:</div>
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs text-gray-600">
                                Ajouté le: {new Date(tenantUser.created_at).toLocaleDateString('fr-FR')}
                              </span>
                              {tenantUser.expires_at && (
                                <span className="text-xs text-orange-600">
                                  Expire le: {new Date(tenantUser.expires_at).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                              {tenantUser.notes && (
                                <span className="text-xs text-gray-600">
                                  Notes: {tenantUser.notes}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <UsersIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Aucun utilisateur trouvé pour ce tenant</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Nombre d'utilisateurs chargés: {tenantUsers?.length || 0}
                      </p>
                      <div className="mt-4 space-x-2">
                        <button
                          onClick={() => loadTenantUsers(selectedTenant.id)}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                        >
                          Recharger les utilisateurs
                        </button>
                        <button
                          type='button'
                          onClick={() => loadTenantUsers(selectedTenant.id)}
                          className="px-3 py-1 text-xs bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors"
                        >
                          Test diagnostic
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type='button'
                  onClick={() => {
                    setShowUsersModal(false);
                    setSelectedTenant(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal pour afficher les sites du tenant */}
        {showSitesModal && selectedTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Sites du tenant</h2>
                  <p className="text-gray-600">{selectedTenant.name} ({selectedTenant.code})</p>
                </div>
                <button
                  onClick={() => {
                    setShowSitesModal(false);
                    setSelectedTenant(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {selectedTenant.sites_info && selectedTenant.sites_info.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTenant.sites_info.map((site) => (
                      <div key={site.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                              <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">{site.name}</h3>
                              <p className="text-sm text-gray-500">Code: {site.code}</p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            site.site_type === 'WAREHOUSE'
                              ? 'bg-blue-100 text-blue-800 border border-blue-200'
                              : site.site_type === 'INDUSTRIAL'
                              ? 'bg-orange-100 text-orange-800 border border-orange-200'
                              : site.site_type === 'MOBILE'
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}>
                            {site.site_type === 'WAREHOUSE' ? 'Entrepôt' :
                             site.site_type === 'INDUSTRIAL' ? 'Industriel' :
                             site.site_type === 'MOBILE' ? 'Mobile' : 'Standard'}
                          </span>
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          {site.address && (
                            <div className="flex items-center">
                              <span className="font-medium mr-2">Adresse:</span>
                              <span>{site.address}</span>
                            </div>
                          )}
                          {site.contact_email && (
                            <div className="flex items-center">
                              <span className="font-medium mr-2">Email:</span>
                              <span>{site.contact_email}</span>
                            </div>
                          )}
                          {site.contact_phone && (
                            <div className="flex items-center">
                              <span className="font-medium mr-2">Téléphone:</span>
                              <span>{site.contact_phone}</span>
                            </div>
                          )}
                          <div className="flex items-center">
                            <span className="font-medium mr-2">Créé le:</span>
                            <span>{new Date(site.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Aucun site trouvé pour ce tenant</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowSitesModal(false);
                    setSelectedTenant(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal d'ajout d'utilisateur au tenant */}
        {showAddUserModal && selectedTenant && (
          <AddTenantUserModal
            isOpen={showAddUserModal}
            onClose={() => {
              setShowAddUserModal(false);
              setSelectedTenant(null);
            }}
            tenantId={selectedTenant.id}
            tenantName={selectedTenant.name}
            onUserAdded={() => {
              // Recharger la liste des tenants pour mettre à jour les compteurs
              loadTenants();
              // Recharger les utilisateurs du tenant sélectionné
              if (selectedTenant) {
                
                loadTenantUsers(selectedTenant.id);
              }
            }}
          />
        )}

        {/* Modal de fiche utilisateur */}
        {showUserProfileModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Fiche utilisateur</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserProfileModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

                <div className="space-y-6">
                  {/* Informations personnelles */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nom d'utilisateur</label>
                        <p className="mt-1 text-sm text-gray-900">{(selectedUser as any).user_username || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{(selectedUser as any).user_email || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Prénom</label>
                        <p className="mt-1 text-sm text-gray-900">{(selectedUser as any).user_first_name || 'Non renseigné'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nom</label>
                        <p className="mt-1 text-sm text-gray-900">{(selectedUser as any).user_last_name || 'Non renseigné'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Informations du tenant */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du tenant</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Rôle</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                              (selectedUser.role as string) === 'TENANT_ADMIN'
                                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                : (selectedUser.role as string) === 'TENANT_MANAGER'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              {(selectedUser.role as string) === 'TENANT_ADMIN' ? 'Administrateur' : 
                               (selectedUser.role as string) === 'TENANT_MANAGER' ? 'Gestionnaire' : 
                               (selectedUser.role as string) === 'TENANT_OWNER' ? 'Propriétaire' : selectedUser.role}
                            </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Ajouté le</label>
                        <p className="mt-1 text-sm text-gray-900">{new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Statut</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                          selectedUser.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedUser.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      {selectedUser.expires_at && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Expire le</label>
                          <p className="mt-1 text-sm text-gray-900">{new Date(selectedUser.expires_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Actions disponibles</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={() => {
                          setShowUserProfileModal(false);
                          handleEditUser(
                            (selectedUser as any).user_id || selectedUser.id,
                            (selectedUser as any).user_username || 'utilisateur'
                          );
                        }}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier les informations
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                        onClick={() => {
                          setShowUserProfileModal(false);
                          handleChangePassword(
                            (selectedUser as any).user_id || selectedUser.id,
                            (selectedUser as any).user_username || 'utilisateur'
                          );
                        }}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Changer le mot de passe
                      </button>
                          {(selectedUser.role as string) !== 'TENANT_OWNER' && (
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                          onClick={() => {
                            setShowUserProfileModal(false);
                            handleRemoveUserFromTenant(
                              (selectedUser as any).tenant_user_id || selectedUser.id,
                              (selectedUser as any).user_id || selectedUser.id,
                              (selectedUser as any).user_username || 'utilisateur'
                            );
                          }}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Détacher du tenant
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserProfileModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
         
        )}

        {/* Modal d'édition d'utilisateur */}
        {showEditUserModal && editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Modifier les informations utilisateur</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditUserModal(false);
                    setEditingUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveUserEdit(); }}>
                <div className="space-y-6">
                  {/* Informations personnelles */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur *</label>
                        <input
                          type="text"
                          value={editFormData.username}
                          onChange={(e) => setEditFormData({...editFormData, username: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                        <input
                          type="text"
                          value={editFormData.first_name}
                          onChange={(e) => setEditFormData({...editFormData, first_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                        <input
                          type="text"
                          value={editFormData.last_name}
                          onChange={(e) => setEditFormData({...editFormData, last_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Statut */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Statut</h3>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={editFormData.is_active}
                        onChange={(e) => setEditFormData({...editFormData, is_active: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Utilisateur actif
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditUserModal(false);
                      setEditingUser(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-bright-orange text-white rounded-xl hover:bg-orange-600 focus:ring-2 focus:ring-orange-500/40 focus:outline-none transition-colors"
                  >
                    Sauvegarder
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de changement de mot de passe */}
        {showChangePasswordModal && editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Changer le mot de passe</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setEditingUser(null);
                    setPasswordFormData({
                      new_password: '',
                      confirm_password: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Utilisateur: <span className="font-medium">{(editingUser as any).user_username || 'N/A'}</span>
                  </p>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSavePasswordChange(); }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe *</label>
                      <input
                        type="password"
                        value={passwordFormData.new_password}
                        onChange={(e) => setPasswordFormData({...passwordFormData, new_password: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Minimum 8 caractères"
                        required
                        minLength={8}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe *</label>
                      <input
                        type="password"
                        value={passwordFormData.confirm_password}
                        onChange={(e) => setPasswordFormData({...passwordFormData, confirm_password: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Répétez le mot de passe"
                        required
                        minLength={8}
                      />
                    </div>
                  </div>

                  {/* Indicateur de correspondance des mots de passe */}
                  {passwordFormData.new_password && passwordFormData.confirm_password && (
                    <div className="mt-3">
                      {passwordFormData.new_password === passwordFormData.confirm_password ? (
                        <div className="flex items-center text-green-600 text-sm">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Les mots de passe correspondent
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600 text-sm">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Les mots de passe ne correspondent pas
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowChangePasswordModal(false);
                        setEditingUser(null);
                        setPasswordFormData({
                          new_password: '',
                          confirm_password: ''
                        });
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={passwordFormData.new_password !== passwordFormData.confirm_password || passwordFormData.new_password.length < 8}
                      className="px-4 py-2 bg-bright-orange text-white rounded-xl hover:bg-orange-600 focus:ring-2 focus:ring-orange-500/40 focus:outline-none transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Changer le mot de passe
                    </button>
                  </div>
                </form>
              </div>
            </div>
         
        )}

        {/* Modal de modification de tenant */}
        {showEditTenantModal && tenantToEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Modifier le tenant</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditTenantModal(false);
                    setTenantToEdit(null);
                    setEditTenantFormData({
                      name: '',
                      code: '',
                      description: '',
                      contact_email: '',
                      contact_phone: '',
                      address: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveTenantEdit(); }}>
                <div className="space-y-6">
                  {/* Informations générales */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom du tenant *</label>
                        <input
                          type="text"
                          value={editTenantFormData.name}
                          onChange={(e) => setEditTenantFormData({...editTenantFormData, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Code du tenant *</label>
                        <input
                          type="text"
                          value={editTenantFormData.code}
                          onChange={(e) => setEditTenantFormData({...editTenantFormData, code: e.target.value.toUpperCase()})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editTenantFormData.description}
                          onChange={(e) => setEditTenantFormData({...editTenantFormData, description: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          rows={3}
                          placeholder="Description du tenant"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Informations de contact */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email de contact</label>
                        <input
                          type="email"
                          value={editTenantFormData.contact_email}
                          onChange={(e) => setEditTenantFormData({...editTenantFormData, contact_email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="contact@tenant.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone de contact</label>
                        <input
                          type="tel"
                          value={editTenantFormData.contact_phone}
                          onChange={(e) => setEditTenantFormData({...editTenantFormData, contact_phone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="+33 1 23 45 67 89"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                        <input
                          type="text"
                          value={editTenantFormData.address}
                          onChange={(e) => setEditTenantFormData({...editTenantFormData, address: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Adresse complète du tenant"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Informations actuelles (lecture seule) */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Informations actuelles</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Statut</label>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                            tenantToEdit.is_active && !tenantToEdit.is_blocked
                              ? 'bg-green-100 text-green-800'
                              : tenantToEdit.is_blocked
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {tenantToEdit.is_active && !tenantToEdit.is_blocked ? 'Actif' : 
                             tenantToEdit.is_blocked ? 'Bloqué' : 'Inactif'}
                          </span>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Créé le</label>
                          <p className="mt-1 text-sm text-gray-900">{new Date(tenantToEdit.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Sites</label>
                          <p className="mt-1 text-sm text-gray-900">{tenantToEdit.sites_count || 0} site(s)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Utilisateurs</label>
                          <p className="mt-1 text-sm text-gray-900">{tenantToEdit.users_count || 0} utilisateur(s)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditTenantModal(false);
                      setTenantToEdit(null);
                      setEditTenantFormData({
                        name: '',
                        code: '',
                        description: '',
                        contact_email: '',
                        contact_phone: '',
                        address: ''
                      });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingEdit || !editTenantFormData.name || !editTenantFormData.code}
                    className="px-4 py-2 bg-bright-orange text-white rounded-xl hover:bg-orange-600 focus:ring-2 focus:ring-orange-500/40 focus:outline-none transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmittingEdit ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Modification...
                      </>
                    ) : (
                      <>
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Sauvegarder les modifications
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression/désactivation */}
        {showDeleteModal && tenantToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {loadingDependencies 
                    ? 'Vérification des dépendances...' 
                    : 'Supprimer définitivement le tenant'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTenantToDelete(null);
                    setTenantDependencies(null);
                    setDeleteReason('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Avertissement */}
                <div className={`p-4 rounded-lg border ${
                  tenantDependencies?.can_delete 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className={`h-5 w-5 mr-2 ${
                      tenantDependencies?.can_delete ? 'text-red-600' : 'text-orange-600'
                    }`} />
                    <div>
                      <h3 className={`font-medium ${
                        loadingDependencies 
                          ? 'text-gray-800' 
                          : (tenantDependencies?.can_delete === true ? 'text-red-800' : 'text-orange-800')
                      }`}>
                        {loadingDependencies 
                          ? '⏳ Vérification en cours...'
                          : (tenantDependencies?.can_delete === true 
                            ? '⚠️ Suppression définitive' 
                            : '⚠️ Suppression impossible')}
                      </h3>
                      <p className={`text-sm mt-1 ${
                        loadingDependencies 
                          ? 'text-gray-700' 
                          : (tenantDependencies?.can_delete === true ? 'text-red-700' : 'text-orange-700')
                      }`}>
                        {loadingDependencies 
                          ? 'Vérification des dépendances du tenant...'
                          : (tenantDependencies?.can_delete === true 
                            ? `Le tenant "${tenantToDelete.name}" sera supprimé définitivement de la base de données. Cette action est irréversible.`
                            : `Le tenant "${tenantToDelete.name}" ne peut pas être supprimé car il contient des dépendances actives. Utilisez le bouton de blocage pour désactiver le tenant.`)}
                      </p>
                      {tenantToDelete.is_blocked && (
                        <p className="text-sm text-yellow-700 mt-2">
                          ℹ️ Note: Ce tenant est actuellement bloqué. La suppression/désactivation est indépendante du blocage.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Informations du tenant */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du tenant</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nom</label>
                        <p className="mt-1 text-sm text-gray-900">{tenantToDelete.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Code</label>
                        <p className="mt-1 text-sm text-gray-900">{tenantToDelete.code}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Statut actuel</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                          tenantToDelete.is_active && !tenantToDelete.is_blocked
                            ? 'bg-green-100 text-green-800'
                            : tenantToDelete.is_blocked
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tenantToDelete.is_active && !tenantToDelete.is_blocked ? 'Actif' : 
                           tenantToDelete.is_blocked ? 'Bloqué' : 'Inactif'}
                        </span>
                      </div>
                      {tenantToDelete.block_reason && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Raison du blocage</label>
                          <p className="mt-1 text-sm text-gray-900">{tenantToDelete.block_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Vérification des dépendances */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Vérification des dépendances</h3>
                  {loadingDependencies ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-500">Vérification des dépendances...</p>
                    </div>
                  ) : tenantDependencies ? (
                    <div className="space-y-4">
                      {/* Résumé des dépendances */}
                      <div className={`p-4 rounded-lg border ${
                        tenantDependencies.can_delete 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex items-center">
                          {tenantDependencies.can_delete ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                          ) : (
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                          )}
                          <div>
                            <h4 className={`font-medium ${
                              tenantDependencies.can_delete === true ? 'text-green-800' : 'text-yellow-800'
                            }`}>
                              {tenantDependencies.can_delete === true 
                                ? 'Suppression définitive possible' 
                                : 'Suppression définitive impossible'}
                            </h4>
                            <p className={`text-sm mt-1 ${
                              tenantDependencies.can_delete === true ? 'text-green-700' : 'text-yellow-700'
                            }`}>
                              {tenantDependencies.can_delete === true 
                                ? 'Ce tenant n\'a aucune dépendance active et peut être supprimé définitivement.'
                                : 'Ce tenant contient des dépendances actives. Utilisez le blocage pour désactiver le tenant.'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Détail des dépendances */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center mb-2">
                            <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                            <h4 className="font-medium text-gray-900">Sites</h4>
                          </div>
                          <p className="text-2xl font-bold text-blue-600">{tenantDependencies.sites.count}</p>
                          <p className="text-sm text-gray-500">Sites actifs</p>
                        </div>
                        
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center mb-2">
                            <UsersIcon className="h-5 w-5 text-green-600 mr-2" />
                            <h4 className="font-medium text-gray-900">Utilisateurs</h4>
                          </div>
                          <p className="text-2xl font-bold text-green-600">{tenantDependencies.tenant_users.count}</p>
                          <p className="text-sm text-gray-500">Utilisateurs du tenant</p>
                        </div>
                        
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center mb-2">
                            <ShieldCheckIcon className="h-5 w-5 text-purple-600 mr-2" />
                            <h4 className="font-medium text-gray-900">Assignations</h4>
                          </div>
                          <p className="text-2xl font-bold text-purple-600">{tenantDependencies.site_assignments.count}</p>
                          <p className="text-sm text-gray-500">Assignations de sites</p>
                        </div>
                      </div>

                      {/* Liste détaillée si nécessaire */}
                      {(tenantDependencies.sites.count > 0 || tenantDependencies.tenant_users.count > 0) && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3">Détail des dépendances</h4>
                          
                          {tenantDependencies.sites.count > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Sites actifs:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {tenantDependencies.sites.data.map((site: any) => (
                                  <li key={site.id} className="flex items-center">
                                    <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                                    {site.name} ({site.code}) - {site.site_type}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {tenantDependencies.tenant_users.count > 0 && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Utilisateurs du tenant:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {tenantDependencies.tenant_users.data.map((user: any) => (
                                  <li key={user.id} className="flex items-center">
                                    <UsersIcon className="h-4 w-4 mr-2" />
                                    Utilisateur ID {user.user_id} - Rôle: {user.role}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                        <span className="text-red-800">Impossible de vérifier les dépendances</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Raison de la suppression/désactivation */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Raison de la suppression
                    <span className="text-gray-400 text-sm font-normal ml-2">(optionnel)</span>
                  </h3>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={3}
                    placeholder="Expliquez la raison de la suppression définitive..."
                    disabled={loadingDependencies || tenantDependencies?.can_delete !== true}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Cette information sera enregistrée dans l'historique pour traçabilité.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setTenantToDelete(null);
                      setTenantDependencies(null);
                      setDeleteReason('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={loadingDependencies || tenantDependencies?.can_delete !== true}
                    className={`px-4 py-2 text-white rounded-xl transition-colors ${
                      tenantDependencies?.can_delete === true
                        ? 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500/40'
                        : 'bg-gray-400 cursor-not-allowed'
                    } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                  >
                    {loadingDependencies 
                      ? 'Chargement...' 
                      : (tenantDependencies?.can_delete === true ? 'Supprimer définitivement' : 'Suppression impossible')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de blocage de tenant */}
        {showBlockModal && tenantToBlock && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Bloquer et désactiver le tenant</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowBlockModal(false);
                    setTenantToBlock(null);
                    setBlockReason('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Avertissement */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">Action importante</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        Le tenant <strong>{tenantToBlock.name}</strong> sera bloqué et désactivé. Il ne pourra plus être utilisé jusqu'à son déblocage.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Informations du tenant */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du tenant</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nom</label>
                        <p className="mt-1 text-sm text-gray-900">{tenantToBlock.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Code</label>
                        <p className="mt-1 text-sm text-gray-900">{tenantToBlock.code}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Statut actuel</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                          tenantToBlock.is_active && !tenantToBlock.is_blocked
                            ? 'bg-green-100 text-green-800'
                            : tenantToBlock.is_blocked
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tenantToBlock.is_active && !tenantToBlock.is_blocked ? 'Actif' : 
                           tenantToBlock.is_blocked ? 'Bloqué' : 'Inactif'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Raison du blocage */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Raison du blocage <span className="text-red-500">*</span></h3>
                  <div className="space-y-2">
                    <input
                      list="blockReasons"
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Sélectionnez ou saisissez une raison..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                    <datalist id="blockReasons">
                      <option value="Violation des conditions d'utilisation" />
                      <option value="Non-paiement" />
                      <option value="Suspension administrative" />
                      <option value="Investigation en cours" />
                      <option value="Demande du client" />
                      <option value="Problème technique" />
                      <option value="Maintenance prévue" />
                      <option value="Non-conformité aux règles" />
                    </datalist>
                    <p className="text-xs text-gray-500">
                      💡 Vous pouvez sélectionner une raison prédéfinie ou saisir votre propre justification.
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBlockModal(false);
                      setTenantToBlock(null);
                      setBlockReason('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmBlock}
                    disabled={!blockReason.trim()}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500/40 focus:outline-none transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <LockClosedIcon className="h-4 w-4 mr-2" />
                    Bloquer le tenant
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SimplePermissionGuard>
  );
};

export default TenantManagementPage;
